import { DateTime } from 'luxon';
import Observation from 'domain/Observation';
import observationActions from 'actions/observationActions';
import lookupTableActions from 'actions/lookupTableActions';
import noteActions from 'actions/noteActions';

export const parseDate = (value) => {
    if (!value || value === '') return null;

    const trimmedValue = String(value).trim();

    if (/^\d+$/.test(trimmedValue)) {
        const excelDate = parseInt(trimmedValue);
        const epoch = DateTime.fromObject({ year: 1900, month: 1, day: 1 });
        const daysToAdd = excelDate > 59 ? excelDate - 2 : excelDate - 1;
        return epoch.plus({ days: daysToAdd }).toJSDate();
    }

    let dt = DateTime.fromISO(trimmedValue);
    if (dt.isValid) return dt.toJSDate();

    dt = DateTime.fromSQL(trimmedValue);
    if (dt.isValid) return dt.toJSDate();

    const jsDate = new Date(trimmedValue);
    if (!isNaN(jsDate.getTime())) return jsDate;

    return null;
};

export const classMap = {
    'Name': 'names',
    'Substantiation': 'substantiations',
    'LocDeterminization': 'locDeterminization',
    'Substrate': 'substrates',
    'ObservationQuality': 'qualities',
    'Location': 'locations',
    'Person': 'persons',
    'Event': 'events',
    'SourceOwned': 'sourceOwneds',
    'Repository': 'repositories',
    'NoteType': 'noteTypes',
    'EvidenceFormat': 'evidenceFormats',
    'Town': 'towns',
    'Keyword': 'keywords',
};

export const fieldsToMap = Observation.FIELDS.filter(field => field.field !== 'label');

export const normalizeString = (str) => {
    return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

export const findPersonByName = (value, personList) => {
    if (!value || typeof value !== 'string') return null;
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) return null;

    const expandedOptionPersonNameList = [];
    personList.forEach((personName) => {
        const fullName = personName.get('name');
        if (!fullName) return;
        const fullNameLower = fullName.toLowerCase();
        const nameParts = fullNameLower.split(' ').filter(part => part.length > 0);

        expandedOptionPersonNameList.push({ name: fullNameLower, personName });

        if (nameParts.length > 1) {
            const firstName = nameParts[0];
            const lastName = nameParts[nameParts.length - 1];
            expandedOptionPersonNameList.push({ name: `${firstName} ${lastName}`, personName });
            expandedOptionPersonNameList.push({ name: `${firstName[0]}. ${lastName}`, personName });
            expandedOptionPersonNameList.push({ name: `${lastName}, ${firstName}`, personName });
            expandedOptionPersonNameList.push({ name: `${lastName}, ${firstName[0]}.`, personName });

            if (nameParts.length > 2) {
                const middleInitials = nameParts.slice(1, -1).map(name => `${name[0]}.`).join(' ');
                expandedOptionPersonNameList.push({ name: `${firstName[0]}. ${middleInitials} ${lastName}`, personName });
                expandedOptionPersonNameList.push({ name: `${firstName} ${middleInitials} ${lastName}`, personName });
                expandedOptionPersonNameList.push({ name: `${lastName}, ${firstName[0]}. ${middleInitials}`, personName });
            }
        }
    });

    const match = expandedOptionPersonNameList.find(({ name }) => name.toLowerCase() === normalizedValue);
    return match?.personName || null;
};

export const parseField = async (field, value, { dispatch, observationOptions, personList }) => {
    if (field.type.type === 'Pointer') {
        if (field.type.targetClass === 'Person') {
            let person = findPersonByName(value, personList);
            if (!person && value && value.trim()) {
                const searchResult = await dispatch(lookupTableActions.searchLookupTable('PersonName', value, 10, 0));
                if (searchResult.value?.results?.length > 0) {
                    person = searchResult.value.results.find((item) =>
                        item?.get('name')?.toLowerCase() === value?.toLowerCase()
                    );
                }
            }
            return person || null;
        } else if (field.type.targetClass === 'Name') {
            const classArrayIdentifier = classMap[field.type.targetClass];
            const classArray = observationOptions[classArrayIdentifier];
            let match = classArray.find((item) =>
                item?.get('name')?.toLowerCase() === value?.toLowerCase()
            );
            if (!match && value && value.trim()) {
                const searchResult = await dispatch(lookupTableActions.searchLookupTable('Name', value, 10, 0));
                if (searchResult.value?.results?.length > 0) {
                    match = searchResult.value.results.find((item) =>
                        item?.get('name')?.toLowerCase() === value?.toLowerCase()
                    );
                }
            }
            return match || null;
        } else if (field.type.targetClass === 'Event') {
            const classArrayIdentifier = classMap[field.type.targetClass];
            const classArray = observationOptions[classArrayIdentifier];
            let match = classArray?.find((item) =>
                item?.get('name')?.toLowerCase() === value?.toLowerCase()
            );
            if (!match && value && value.trim()) {
                const searchResult = await dispatch(lookupTableActions.searchLookupTable(field.type.targetClass, value, 10, 0));
                if (searchResult.value?.results?.length > 0) {
                    match = searchResult.value.results.find((item) =>
                        item?.get('name')?.toLowerCase() === value?.toLowerCase()
                    );
                }
            }
            return match || null;
        } else {
            const classArrayIdentifier = classMap[field.type.targetClass];
            const classArray = observationOptions[classArrayIdentifier];
            const lowerValue = value?.toLowerCase();
            let match = classArray?.find((item) =>
                item?.get('shortCode')?.toLowerCase() === lowerValue
            ) || classArray?.find((item) =>
                item?.get('label')?.toLowerCase() === lowerValue
            );
            if (!match && value && value.trim()) {
                const searchResult = await dispatch(lookupTableActions.searchLookupTable(field.type.targetClass, value, 10, 0));
                if (searchResult.value?.results?.length > 0) {
                    match = searchResult.value.results.find((item) =>
                        item?.get('shortCode')?.toLowerCase() === lowerValue
                    ) || searchResult.value.results.find((item) =>
                        item?.get('label')?.toLowerCase() === lowerValue
                    );
                }
            }
            return match || null;
        }
    } else if (field.type === 'Boolean') {
        const truthyValues = ['true', 'yes', '1', 't', 'y'];
        return truthyValues.includes(value.toLowerCase());
    } else if (field.type === 'Date') {
        return parseDate(value);
    } else if (field.type === 'Number') {
        if (!value || value.trim() === '') return null;
        const num = parseFloat(value);
        return isNaN(num) ? value : num;
    } else {
        return value;
    }
};

export const createObservationForSave = (observation) => {
    const newObservation = new Observation();
    fieldsToMap.forEach((field) => {
        if (field.type.type === 'Pointer') {
            if (field.type.targetClass === 'Person') {
                newObservation.set(field.field, observation.get(field.field)?.get('person')?.id);
            } else {
                newObservation.set(field.field, observation.get(field.field)?.id);
            }
        } else {
            newObservation.set(field.field, observation.get(field.field));
        }
    });
    return newObservation;
};

export const cleanParsedData = (parsed) => {
    const fields = parsed.meta.fields.filter(f => f.trim());
    const datePartCols = ['Obs_Month', 'Obs_Day', 'Obs_Year'];
    const hasDateParts = datePartCols.every(c => fields.includes(c));
    const mergedNotesCol = fields.find(f => f.includes(' ') && f.split(' ').every(part => part.includes('_Notes') || part.includes('Notes')));

    const data = parsed.data.map(row => {
        const cleaned = {};
        fields.forEach(f => { cleaned[f] = row[f]; });

        if (hasDateParts) {
            const m = (row['Obs_Month'] || '').trim();
            const d = (row['Obs_Day'] || '').trim();
            const y = (row['Obs_Year'] || '').trim();
            if (m && d && y) cleaned['Date'] = `${m}/${d}/${y}`;
            else if (m && y) cleaned['Date'] = `${m}/${y}`;
            else if (y) cleaned['Date'] = y;
            else cleaned['Date'] = '';
            datePartCols.forEach(c => delete cleaned[c]);
        }

        if (mergedNotesCol) {
            const parts = mergedNotesCol.split(' ');
            const val = (row[mergedNotesCol] || '').trim();
            parts.forEach((part, i) => { cleaned[part] = i === 0 ? val : ''; });
            delete cleaned[mergedNotesCol];
        }

        return cleaned;
    });

    let cleanFields = fields.filter(f => !datePartCols.includes(f) && f !== mergedNotesCol);
    if (hasDateParts) {
        const eventIdx = cleanFields.indexOf('Event_ID');
        cleanFields.splice(eventIdx >= 0 ? eventIdx + 1 : cleanFields.length, 0, 'Date');
    }
    if (mergedNotesCol) {
        const parts = mergedNotesCol.split(' ');
        const idx = cleanFields.indexOf(mergedNotesCol);
        if (idx === -1) cleanFields.push(...parts);
        else cleanFields.splice(idx, 1, ...parts);
    }

    return { data, fields: cleanFields };
};

export const attemptMapping = (uploadFields, existingMapping) => {
    if (!uploadFields.length) return { mapping: existingMapping, isComplete: false };

    const newMapping = { ...existingMapping };
    const normalizedUploadFields = uploadFields.map(normalizeString);

    fieldsToMap.forEach((field) => {
        const normalizedField = normalizeString(field.field);
        const normalizedLabel = normalizeString(field.label);

        const fieldIndex = normalizedUploadFields.findIndex(f => f === normalizedField);
        const labelIndex = normalizedUploadFields.findIndex(f => f === normalizedLabel);

        if (fieldIndex !== -1) {
            newMapping[field.field] = uploadFields[fieldIndex];
        } else if (labelIndex !== -1) {
            newMapping[field.field] = uploadFields[labelIndex];
        }
    });

    const isComplete = Object.values(newMapping).every((v) => v !== null);
    return { mapping: newMapping, isComplete };
};

export const mapAndValidateRows = async (uploadData, mapping, { dispatch, observationOptions, personList }) => {
    const observationNoteTypes = observationOptions.noteTypes?.filter(noteType =>
        noteType.get('filteredFor') === 'Observation'
    ) || [];

    const mappedObservations = await Promise.all(uploadData.map(async (row) => {
        const observation = new Observation();
        const notes = [];
        const warnings = {};

        for (const field of fieldsToMap) {
            const mappedFieldName = mapping[field.field];
            if (mappedFieldName && row[mappedFieldName] !== undefined) {
                const rawValue = row[mappedFieldName];
                const fieldValue = await parseField(field, rawValue, { dispatch, observationOptions, personList });
                if (fieldValue === null && rawValue && rawValue.trim()) {
                    warnings[field.field] = rawValue;
                }
                observation.set(field.field, fieldValue);
            }
        }

        for (const noteType of observationNoteTypes) {
            const fieldKey = `note_${noteType.id}`;
            const mappedFieldName = mapping[fieldKey];
            if (mappedFieldName && row[mappedFieldName] && row[mappedFieldName].trim()) {
                notes.push({ type: noteType, text: row[mappedFieldName].trim() });
            }
        }

        try {
            const observationToSave = createObservationForSave(observation);
            await observationToSave.inspect();
            const result = { observation, notes, row };
            if (Object.keys(warnings).length > 0) result.warnings = warnings;
            return result;
        } catch (err) {
            const observationErrors = err.inner?.reduce((acc, curr) => {
                const fieldName = curr.path;
                const mappedField = mapping[fieldName];
                const originalValue = mappedField ? row[mappedField] : null;
                acc[fieldName] = originalValue || curr.message;
                return acc;
            }, {});
            return { observation, notes, row, errors: observationErrors, warnings };
        }
    }));

    return {
        valid: mappedObservations.filter(({ errors }) => !errors),
        errors: mappedObservations.filter(({ errors }) => errors)
    };
};

export const saveObservation = async (observation, notes, dispatch) => {
    const observationToSave = createObservationForSave(observation);
    const savedObservationRequest = await dispatch(observationActions.save(observationToSave));
    const savedId = savedObservationRequest.value.id;

    if (notes && notes.length > 0) {
        await Promise.all(notes.map(note =>
            dispatch(noteActions.save({
                observation: savedId,
                type: note.type.id,
                text: note.text
            }))
        ));
    }

    const fullObservation = await dispatch(observationActions.get(savedId));
    return fullObservation.value;
};
